import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import WaveformCanvas, { type WaveformCanvasHandle } from "./WaveformCanvas";
import { sendBlobToBackend, getTokenOrRefresh } from "../utils";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { useAudioRecorder } from "react-audio-voice-recorder";
import TypingBubble from "./TypingBubble";
import { useNavigate } from "react-router-dom";

// Types
interface AzureToken {
  authToken: string | null;
  region?: string;
  error?: string;
}

interface ChatInputProps {
  url: string;
  azureToken: AzureToken | null;
  onComplete: (transcript: string[], botResponse: any) => void;
  setAzureToken: (token: AzureToken) => void;
  setError: (error: boolean) => void;
  refreshCheckList?: () => void;
  setAudioBlob: (blob: Blob) => void;
  showInputElement: boolean;
  stopTimer?: () => number;
  currentResponseTime?: number;
  isDialogOpen?: boolean; // Add this new prop
  imagePath?: string | null; // Add imagePath prop for fullscreen mode
  speakingStyle?: string; // Add speakingStyle prop for fullscreen mode
  isFullscreen?: boolean; // Add fullscreen state from parent
  onToggleFullscreen?: () => void; // Add fullscreen toggle function from parent
  onMediaStreamChange?: (stream: MediaStream | null) => void; // Add media stream callback
  onRecordingStateChange?: (isRecording: boolean) => void; // Add recording state callback
  onRecordingMethodsChange?: (methods: { startRecording: () => void; stopRecording: () => void }) => void; // Add recording methods callback
  onRecordingStopped?: () => void; // Add callback for when recording stops
  onRecognizerReady?: (ready: boolean) => void; // Notify parent when recognizer is ready
  onProcessingChange?: (isProcessing: boolean) => void; // Notify parent when processing starts/ends
}

interface RecordingState {
  isRecording: boolean;
  showStopBtn: boolean;
  userPressedStop: boolean;
  speechEndDetected: boolean;
  isProcessing: boolean;
}

const ChatInput = forwardRef<{ startRecording: () => void; stopRecording: () => void }, ChatInputProps>(({
  url,
  azureToken,
  onComplete,
  setAzureToken,
  setError,
  setAudioBlob,
  showInputElement,
  currentResponseTime,
  stopTimer,
  isDialogOpen = false, // Add this with default value
  imagePath = null,
  speakingStyle = '',
  isFullscreen = false,
  onToggleFullscreen,
  onMediaStreamChange,
  onRecordingStateChange,
  onRecordingMethodsChange,
  onRecordingStopped,
  onRecognizerReady,
  onProcessingChange,
}, ref) => {
  // Audio and recognition states
  const [blob, setBlob] = useState<Blob>();
  const [recognizer, setRecognizer] = useState<sdk.SpeechRecognizer>();
  const [transcript, setTranscript] = useState<string[]>([]);
  const transcriptRef = useRef<string[]>(transcript);
  const recorder = useAudioRecorder();

  // UI states
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    showStopBtn: false,
    userPressedStop: false,
    speechEndDetected: false,
    isProcessing: false,
  });
  const [isTyping, setIsTyping] = useState(false);
  const [azRecordingFailed, setAzRecordingFailed] = useState(false);
  const [isRecognizerReady, setIsRecognizerReady] = useState(false);

  // Waveform reusable component ref
  const waveformRef = useRef<WaveformCanvasHandle | null>(null);
  const currentStreamRef = useRef<MediaStream | null>(null);

  const navigate = useNavigate();


  // Also communicate when the ref is set up from media stream
  const onMediaStreamChangeRef = useRef(onMediaStreamChange);
  
  // Update ref when callback changes
  useEffect(() => {
    onMediaStreamChangeRef.current = onMediaStreamChange;
  }, [onMediaStreamChange]);

  const setupWaveformFromMic = useCallback((stream: MediaStream) => {
    console.log('setupWaveformFromMic called with stream:', stream);
    console.log('waveformRef.current:', waveformRef.current);
    if (waveformRef.current) {
      console.log('Setting up waveform from media stream');
      waveformRef.current.setupFromMediaStream(stream);
    } else {
      console.warn('waveformRef.current is null, cannot setup waveform');
    }
    // Only notify parent if stream has changed to avoid infinite loops
    if (onMediaStreamChangeRef.current && currentStreamRef.current !== stream) {
      currentStreamRef.current = stream;
      onMediaStreamChangeRef.current(stream);
    }
  }, []);

  // Communicate recording state to parent
  const onRecordingStateChangeRef = useRef(onRecordingStateChange);
  
  useEffect(() => {
    onRecordingStateChangeRef.current = onRecordingStateChange;
  }, [onRecordingStateChange]);

  useEffect(() => {
    if (onRecordingStateChangeRef.current) {
      onRecordingStateChangeRef.current(recorder.isRecording);
    }
  }, [recorder.isRecording]);

  // Azure Speech recognizer setup
  const setupAzureRecognizer = (tokenObj: AzureToken): sdk.SpeechRecognizer => {
    if (!tokenObj.authToken || !tokenObj.region || tokenObj.error) {
      throw new Error("Invalid token configuration");
    }

    const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
      tokenObj.authToken,
      tokenObj.region
    );
    speechConfig.speechRecognitionLanguage = "en-US";
    return new sdk.SpeechRecognizer(
      speechConfig,
      sdk.AudioConfig.fromDefaultMicrophoneInput()
    );
  };

  // Initialize recognizer
  useEffect(() => {
    const initializeRecognizer = async () => {
      console.log("initializing recognizer");
      try {
        const tokenObj = await getTokenOrRefresh();
        if (tokenObj.error) {
          console.error('Token refresh failed:', tokenObj.error);
          throw new Error("Failed to refresh token");
        }

        setAzureToken(tokenObj);
        const currRecognizer = setupAzureRecognizer(tokenObj);

        // Set up recognizer event handlers
        currRecognizer.recognized = (_, e) => {
          if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
            const newTranscript = [...transcriptRef.current, e.result.text];
            setTranscript(newTranscript);
            transcriptRef.current = newTranscript;
          } else {
            console.warn('Speech not recognized:', e.result.reason);
          }
        };

        currRecognizer.speechStartDetected = () =>
          setRecordingState(prev => ({ ...prev, speechEndDetected: false }));
        currRecognizer.speechEndDetected = () =>
          setRecordingState(prev => ({ ...prev, speechEndDetected: true }));
        currRecognizer.canceled = handleRecognizerCancellation;

        setRecognizer(currRecognizer);
        setIsRecognizerReady(true);
        if (onRecognizerReady) {
          onRecognizerReady(true);
        }
      } catch (error) {
        console.error("Error initializing recognizer:", {
          error,
          stack: error instanceof Error ? error.stack : undefined
        });
        handleError();
      }
    };

    if (!recognizer && !azRecordingFailed) {
      initializeRecognizer();
    }
    return () => {
      console.log("unmounting recognizer");
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
      }
    }
  }, [azRecordingFailed]); // Remove recognizer from dependencies to prevent infinite loop

  // Handle recognizer cancellation
  const handleRecognizerCancellation = (s: sdk.Recognizer, e: sdk.CancellationEventArgs) => {
    console.error('Recognizer cancelled:', {
      errorCode: e.errorCode,
      errorDetails: e.errorDetails
    });

    if (e.errorCode === sdk.CancellationErrorCode.AuthenticationFailure) {
      console.warn('Authentication failure, attempting token refresh');
      refreshRecognizerToken();
    } else {
      handleError();
    }
  };

  // Error handling
  const handleError = () => {
    console.error('Recording failed, setting error state');
    setError(true);
    setAzRecordingFailed(true);
    setIsRecognizerReady(false);
    if (onRecognizerReady) {
      onRecognizerReady(false);
    }
  };

  // Token refresh
  const refreshRecognizerToken = async () => {
    try {
      const tokenObj = await getTokenOrRefresh();
      if (tokenObj.error) {
        console.error('Token refresh failed:', tokenObj.error);
        throw new Error("Failed to refresh token");
      }

      setAzureToken(tokenObj);
      setRecognizer(setupAzureRecognizer(tokenObj));
      setError(false);
      setIsRecognizerReady(true);
      if (onRecognizerReady) {
        onRecognizerReady(true);
      }
    } catch (error) {
      console.error("Error refreshing token:", {
        error,
        stack: error instanceof Error ? error.stack : undefined
      });
      handleError();
    }
  };

  // Handle recording blob updates
  useEffect(() => {
    if (!recorder.isRecording && recorder.recordingBlob) {
      setBlob(recorder.recordingBlob);
    }
  }, [recorder.recordingBlob, recorder.isRecording]);

  // Add this debug function
  const logStateOnStop = () => {
    console.log('Recording Stop State:', {
      isProcessing: recordingState.isProcessing,
      hasBlob: !!blob,
      transcriptLength: transcriptRef.current.length,
      userPressedStop: recordingState.userPressedStop,
      speechEndDetected: recordingState.speechEndDetected,
      isRecording: recorder.isRecording,
      recognizerState: recognizer?.recognizing
    });
  };

  // Create refs for callbacks
  const onRecordingStoppedRef = useRef(onRecordingStopped);
  const onProcessingChangeRef = useRef(onProcessingChange);
  
  // Update refs when props change
  useEffect(() => {
    onRecordingStoppedRef.current = onRecordingStopped;
    onProcessingChangeRef.current = onProcessingChange;
  }, [onRecordingStopped, onProcessingChange]);

  // Modify the stopRecording function
  const stopRecording = useCallback(() => {
    try {
      console.log('Stop recording initiated');
      if (!recorder.isRecording) {
        console.warn('Stop recording called while not recording');
        return;
      }
      recorder.stopRecording();
      recognizer?.stopContinuousRecognitionAsync();
      setRecordingState(prev => ({
        ...prev,
        showStopBtn: false,
        userPressedStop: true
      }));
      logStateOnStop();
      
      // Notify parent that recording has stopped
      if (onRecordingStoppedRef.current) {
        onRecordingStoppedRef.current();
      }
    } catch (error) {
      console.error("Error stopping recording:", {
        error,
        stack: error instanceof Error ? error.stack : undefined
      });
      handleError();
    }
  }, [recorder, recognizer]);

  // Create refs for stable function references
  const onCompleteRef = useRef(onComplete);
  const setAudioBlobRef = useRef(setAudioBlob);
  
  // Create refs for recording state to avoid infinite loops
  const recordingStateRef = useRef(recordingState);
  
  // Update refs when props change
  useEffect(() => {
    onCompleteRef.current = onComplete;
    setAudioBlobRef.current = setAudioBlob;
  }, [onComplete, setAudioBlob]);
  
  // Update recording state ref whenever recordingState changes
  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  // Trigger effect when recording state changes - this will cause the sendData effect to re-run
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    setTrigger(prev => prev + 1);
  }, [recordingState.userPressedStop, recordingState.speechEndDetected, recordingState.isProcessing]);

  // Effect to trigger data sending when specific conditions are met
  useEffect(() => {
    const { isProcessing, userPressedStop, speechEndDetected } = recordingState;
    
    // Only trigger if we have all the required conditions
    if (!isProcessing && blob && transcriptRef.current.length > 0 && userPressedStop && speechEndDetected) {
      const sendData = async () => {
        console.log('Send Data Check:', {
          isProcessing,
          hasBlob: !!blob,
          transcriptLength: transcriptRef.current.length,
          speechEndDetected,
          allConditionsMet: true
        });

        setRecordingState(prev => ({ ...prev, isProcessing: true }));
        if (onProcessingChangeRef.current) {
          onProcessingChangeRef.current(true);
        }
        setIsTyping(true);

        try {
          const botResponse = await sendBlobToBackend(url, blob, transcript.join(". "), currentResponseTime || 0);
          if (botResponse.status === 401) {
            console.error('Unauthorized access, redirecting to error page');
            navigate(`/error/${botResponse.status}`);
            return;
          }

          // Reset states and complete
          setIsTyping(false);
          setRecordingState(prev => ({
            ...prev,
            speechEndDetected: false,
            userPressedStop: false,
            isProcessing: false
          }));
          if (onProcessingChangeRef.current) {
            onProcessingChangeRef.current(false);
          }
          setTranscript([]);
          transcriptRef.current = [];

          onCompleteRef.current(transcript, botResponse);
          if (recorder.recordingBlob) {
            setAudioBlobRef.current(recorder.recordingBlob);
          }
        } catch (error) {
          console.error("Error sending data to backend:", {
            error,
            stack: error instanceof Error ? error.stack : undefined,
            url,
            transcriptLength: transcript.length
          });
          handleError();
          if (onProcessingChangeRef.current) {
            onProcessingChangeRef.current(false);
          }
        }
      };

      sendData();
    }
  }, [blob, url, currentResponseTime, navigate, trigger]); // Include trigger to re-run when recording state changes


  const teardownWaveform = useCallback(() => {
    waveformRef.current?.teardown();
    currentStreamRef.current = null;
    // Don't call onMediaStreamChange in teardown to avoid circular dependency
    // The parent will handle stream cleanup when needed
  }, []);

  // Start/stop waveform tied to recording state
  useEffect(() => {
    console.log('Main waveform setup check:', {
      isRecording: recorder.isRecording,
      hasMediaRecorder: !!recorder.mediaRecorder,
      hasStream: !!(recorder.mediaRecorder && (recorder.mediaRecorder as any).stream)
    });
    
    if (recorder.isRecording && recorder.mediaRecorder && (recorder.mediaRecorder as any).stream) {
      const stream: MediaStream = (recorder.mediaRecorder as any).stream;
      console.log('Main waveform setup: Setting up from mic stream');
      setupWaveformFromMic(stream);
    } else {
      console.log('Main waveform setup: Tearing down waveform');
      teardownWaveform();
    }

    return () => {
      // On unmount or deps change, ensure cleanup
      if (!recorder.isRecording) {
        teardownWaveform();
      }
    };
  }, [recorder.isRecording, recorder.mediaRecorder, setupWaveformFromMic, teardownWaveform]);

  // Additional waveform setup when recording state changes - REMOVED to prevent infinite loop
  // The main waveform setup useEffect above should handle all cases

  // Fullscreen toggle - now handled by parent
  const toggleFullscreen = () => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    }
  };

  // Recording controls
  const startRecording = useCallback(() => {
    if (!isRecognizerReady) {
      console.warn('Attempted to start recording before recognizer was ready');
      return;
    }

    // Stop timer when user starts recording
    if (stopTimer) {
      stopTimer();
    }

    setTranscript([]);
    setRecordingState(prev => ({
      ...prev,
      showStopBtn: true,
      userPressedStop: false
    }));
    recorder.startRecording();
    recognizer?.startContinuousRecognitionAsync();
  }, [isRecognizerReady, stopTimer, recorder, recognizer]);

  // Store stable references to recording methods
  const recordingMethodsRef = useRef({ startRecording, stopRecording });
  const onRecordingMethodsChangeRef = useRef(onRecordingMethodsChange);
  
  // Update refs when callbacks change
  useEffect(() => {
    onRecordingMethodsChangeRef.current = onRecordingMethodsChange;
  }, [onRecordingMethodsChange]);
  
  // Update the ref when methods change - but don't notify parent to avoid infinite loops
  // The parent can access methods via the ref when needed
  useEffect(() => {
    recordingMethodsRef.current = { startRecording, stopRecording };
  }, [startRecording, stopRecording]);

  // Expose recording methods to parent via ref
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording
  }), [startRecording, stopRecording]);


  // Keyboard controls
  useEffect(() => {
    // Don't add listeners if dialog is open
    if (isDialogOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.id === "summary-box") return;
      if (e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        if (!recorder.isRecording) {
          startRecording();
        }
      }
      // Add F key for fullscreen toggle
      // if (e.code === 'KeyF') {
      //   e.preventDefault();
      //   e.stopPropagation();
      //   toggleFullscreen();
      // }
      // ESC key handling is managed by parent ChatDetails component
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (document.activeElement?.id === "summary-box") return;
      if (e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        if (recorder.isRecording) {
          stopRecording();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [recorder.isRecording, isRecognizerReady, isDialogOpen, startRecording, stopRecording, toggleFullscreen]); // Add all dependencies

  return (
    <div>
      {azRecordingFailed && (
        <div className="z-50">
          <div id="error-message" className="text-red-500 text-sm font">
            Refresh this page to continue from where you left off
            <button
              className="text-red-500 hover:text-red-700 focus:outline-none"
              onClick={() => setAzRecordingFailed(false)}
            >
              <svg width="20" height="20" viewBox="0 0 40 40">
                <path d="M 10,10 L 20,20 M 20,10 L 10,20" stroke="black" strokeWidth="2" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {showInputElement && (
        <div className="flex space-x-4 justify-center">
          {isTyping && <TypingBubble />}
          <div>
            
            {/* Waveform display when recording is active but not in stop button state */}
            {!isTyping && recorder.isRecording && !recordingState.showStopBtn && (
              <div className="flex flex-col items-center space-y-4">
                <div className="text-sm text-gray-600">Recording...</div>
                <WaveformCanvas ref={waveformRef} width={300} height={100} isBotSpeaking={false} />
              </div>
            )}
            
            {!isTyping && !recordingState.showStopBtn && (
              <div className="flex items-center">
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  onClick={startRecording}
                >
                  Start recording
                </button>
                <div className="relative ml-2 group">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded p-2 w-64 z-10">
                    <p className="text-center">
                      Press and hold spacebar to record, release to stop.
                      <br />
                      Or click the button to start and stop recording.
                      <br />
                      Click the expand icon on the image to enter fullscreen mode.
                    </p>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
            )}
            {!isTyping && recordingState.showStopBtn && (
              <div className="flex flex-col items-center space-y-4">
                 <WaveformCanvas ref={waveformRef} width={300} height={100} isBotSpeaking={false} />
                <div className="flex items-center">
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    onClick={stopRecording}
                  >
                    Stop recording
                  </button>
                  <div className="relative ml-2 group">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded p-2 w-64 z-10">
                        <p className="text-center">
                          Press and hold spacebar to record, release to stop.
                          <br />
                          Or click the button to start and stop recording.
                        </p>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                </div>
                {/* Waveform display during recording */}
               
              </div>
            )}
          </div>
        </div>
      )}

     
    </div>
  );
});

export default ChatInput;
